import { create } from 'zustand';
import { employeeService } from '../services/employeeService';
import { courseService } from '../services/courseService';
import { certificateService } from '../services/certificateService';
import { Employee, EmployeeTraining } from '../services/employeeService';
import { Course, Certificate } from '../types';

interface AdminState {
  employees: Employee[];
  employeeTraining: EmployeeTraining[];
  courses: Course[];
  certificates: Certificate[];
  stats: any;
  recentActivity: any;
  pendingInvites: any[];
  isDueTrainingModalOpen: boolean;
  isLoading: boolean;
  error: string | null;
  fetchAdminData: () => Promise<void>;
  fetchEmployeesOnly: () => Promise<void>;
  fetchCriticalDashboardData: () => Promise<void>;
  openDueTrainingModal: () => void;
  closeDueTrainingModal: () => void;
  clearAdminData: () => void;
}

export const useAdminStore = create<AdminState>((set) => ({
  // Initial state - no localStorage persistence
  employees: [],
  employeeTraining: [],
  courses: [],
  certificates: [],
  stats: null,
  recentActivity: null,
  pendingInvites: [],
  isDueTrainingModalOpen: false,
  isLoading: false,
  error: null,

  clearAdminData: () => {
    set({
      employees: [],
      employeeTraining: [],
      courses: [],
      certificates: [],
      stats: null,
      recentActivity: null,
      pendingInvites: [],
      isDueTrainingModalOpen: false,
      isLoading: false,
      error: null
    });
  },

  fetchAdminData: async () => {
    try {
      set({ isLoading: true, error: null });
      
      // Fetch critical data first (stats and employees)
      const [stats, employees] = await Promise.all([
        employeeService.getEmployeeStats(),
        employeeService.getAllEmployees()
      ]);
      
      // Update state with critical data first
      set({ 
        stats, 
        employees, 
        isLoading: false 
      });
      
      // Then fetch secondary data in background
      const [training, courses, certificates, activity, pendingInvites] = await Promise.all([
        employeeService.getEmployeeTraining().then(res => res.data),
        courseService.getAdminCourses(),
        certificateService.getAllCertificates(),
        courseService.getDashboardActivity(),
        employeeService.getPendingInvitations()
      ]);

      // Update with secondary data - no localStorage persistence
      set({
        employees,
        employeeTraining: training,
        courses,
        certificates,
        stats,
        recentActivity: activity,
        pendingInvites,
        isDueTrainingModalOpen: false,
        isLoading: false
      });
    } catch (error) {
      console.error('Error in fetchAdminData:', error);
      set({ 
        error: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false,
        pendingInvites: []
      });
    }
  },

  openDueTrainingModal: () => {
    set({ isDueTrainingModalOpen: true });
  },

  closeDueTrainingModal: () => {
    set({ isDueTrainingModalOpen: false });
  },

  fetchEmployeesOnly: async () => {
    try {
      set({ isLoading: true, error: null });
      const employees = await employeeService.getAllEmployees();
      set({ 
        employees,
        isLoading: false 
      });
    } catch (error) {
      console.error('Error fetching employees:', error);
      set({ 
        error: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false,
        employees: []
      });
    }
  },

  fetchCriticalDashboardData: async () => {
    try {
      set({ isLoading: true, error: null });
      
      // Only fetch the most critical data for initial dashboard load
      const [stats, employees] = await Promise.all([
        employeeService.getEmployeeStats(),
        employeeService.getAllEmployees()
      ]);
      
      set({ 
        stats, 
        employees, 
        isLoading: false 
      });
    } catch (error) {
      console.error('Error fetching critical dashboard data:', error);
      set({ 
        error: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false
      });
    }
  }
}));